import React from "react";
import { IconButton, Card, CardText, Checkbox } from "material-ui";
import moment from "moment";
import Edit from "material-ui/svg-icons/editor/mode-edit";
import Vip from "material-ui/svg-icons/communication/email";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { commitMutation, graphql } from "react-relay";
import PropTypes from "prop-types";

import environment from "../../relay/environment";
import DailyRates from "./DailyRates";
import EditSegment from "./EditSegment";
import NoteListItem from "./NoteListItem";

const DisplayData = ({
  segment: {
    category,
    smokingRoom,
    numAdults,
    numChildren,
    startDate,
    endDate,
    cancellationDeadline,
    sabreLocator,
    confirmationNumber,
    cancellationNumber,
    rates,
    currency,
    averageDailyRate,
    notes,
    bedType,
    amenityProgram,
    vendor
  }
}) => {
  const displayCategory = category !== "" ? category : "no category set";
  const displayBedType = bedType !== "" ? bedType : "no bedding set";
  const displayAmenity =
    amenityProgram !== "" ? amenityProgram : "no amenity program set";
  const smoking = smokingRoom ? "smoking" : "non-smoking";
  const formatDate = date =>
    moment(date)
      .format("DDMMM YYYY")
      .toUpperCase();
  const numNotes = notes.length;

  return (
    <div>
      <h3>
        {vendor ? (
          <Link to={`/vendors/${vendor.id}/`}>{vendor.name}</Link>
        ) : (
          "TBD"
        )}
      </h3>
      <p>
        {displayAmenity}, {displayCategory}, {displayBedType}, {smoking},&nbsp;
        {numAdults} adults, {numChildren} children
      </p>
      <div style={styles.detailColumns}>
        <div style={styles.detailColumn}>
          <span>Check In: {formatDate(startDate)}</span>
          <span>Check Out: {formatDate(endDate)}</span>
          <span>Cancellation: {formatDate(cancellationDeadline)}</span>
        </div>
        <div style={styles.detailColumn}>
          <span>SABRE Locator: {sabreLocator}</span>
          <span>Confirmation Number: {confirmationNumber}</span>
          <span>Cancellation Number: {cancellationNumber}</span>
        </div>
      </div>
      <DailyRates
        rates={rates}
        currency={currency}
        average={averageDailyRate}
      />
      <h5>Notes ({numNotes})</h5>
      {numNotes > 0 ? (
        <ul>
          {notes.map((note, index) => (
            <NoteListItem key={index} index={index} note={note} />
          ))}
        </ul>
      ) : null}
    </div>
  );
};

DisplayData.propTypes = {
  segment: PropTypes.shape({
    category: PropTypes.string,
    smokingRoom: PropTypes.bool,
    numAdults: PropTypes.number,
    numChildren: PropTypes.number,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    cancellationDeadline: PropTypes.string,
    sabreLocator: PropTypes.string,
    confirmationNumber: PropTypes.string,
    cancellationNumber: PropTypes.string,
    rates: PropTypes.arrayOf(PropTypes.number),
    currency: PropTypes.string,
    averageDailyRate: PropTypes.numbe,
    notes: PropTypes.arrayOf(PropTypes.object),
    bedType: PropTypes.string,
    amenityProgram: PropTypes.string,
    vendor: PropTypes.obects
  }).isRequired
};

@connect(state => ({
  auth: state.Auth.toJS()
}))
class SegmentDetails extends React.Component {
  static contextTypes = {
    router: PropTypes.object
  };

  static propTypes = {
    segment: PropTypes.shape({
      id: PropTypes.string,
      dropbox: PropTypes.bool,
      invoiced: PropTypes.bool,
      vipDrafted: PropTypes.bool,
      cancelled: PropTypes.bool,
      trip: PropTypes.object
    }).isRequired,
    auth: PropTypes.shape({
      authenticated: PropTypes.bool,
      groups: PropTypes.arrayOf(PropTypes.string)
    }).isRequired
  };

  constructor(props) {
    super(props);

    this.state = {
      editing: false
    };
  }

  setCheckbox = checkbox => (e, checked) => {
    const {
      segment: { id: segmentId }
    } = this.props;

    commitMutation(environment, {
      mutation: graphql`
        mutation SegmentDetails_SetCheckbox_Mutation(
          $input: SetCheckboxInput!
        ) {
          setCheckbox(input: $input) {
            segment {
              id
              dropbox
              invoiced
              vipDrafted
              booked
              cancelled
              trip {
                dropbox
                invoice
                draftVip
                book
                cancel
                actionableInvoice
              }
            }
          }
        }
      `,
      variables: { input: { segmentId, checkbox, value: checked } },
      onError: err => alert(err)
    });
  };

  toggleEditing = () => {
    this.setState({ editing: !this.state.editing });
  };

  composeVIP = () => {
    const {
      segment: {
        id: segmentId,
        trip: { id: tripId }
      }
    } = this.props;

    this.context.router.push(`/trips/${tripId}/segments/${segmentId}/`);
  };

  render() {
    const { editing } = this.state;
    const {
      segment,
      auth: { authenticated, groups }
    } = this.props;
    const {
      dropbox,
      invoiced,
      vipDrafted,
      cancelled,
      id: segmentId,
      trip: { id: tripId }
    } = segment;

    const authorized = authenticated && groups.indexOf("admin") > -1;
    const locked = cancelled && !authorized;

    return (
      <Card style={styles.segmentDetailStyle}>
        <CardText>
          <div style={styles.segmentStatus}>
            <h4>Status:</h4>
            <div style={styles.checkbox}>
              <Checkbox
                label="Filed"
                labelPosition="right"
                checked={dropbox}
                onCheck={this.setCheckbox("dropbox")}
                iconStyle={styles.checkboxIconStyle}
                disabled={locked}
              />
            </div>
            <div style={styles.checkbox}>
              <Checkbox
                label="Invoiced"
                labelPosition="right"
                checked={invoiced}
                onCheck={this.setCheckbox("invoiced")}
                iconStyle={styles.checkboxIconStyle}
                disabled={locked}
              />
            </div>
            <div style={styles.checkbox}>
              <Checkbox
                label="VIP Drafted"
                labelPosition="right"
                checked={vipDrafted}
                onCheck={this.setCheckbox("vipDrafted")}
                iconStyle={styles.checkboxIconStyle}
                disabled={locked}
              />
            </div>
          </div>
          <h4>
            Details
            <IconButton
              tooltip="Edit Segment"
              disabled={editing || locked}
              onTouchTap={this.toggleEditing}
            >
              <Edit />
            </IconButton>
            {authorized && segment.vendor ? (
              <Link to={`/trips/${tripId}/segments/${segmentId}/`}>
                <Vip />
              </Link>
            ) : null}
          </h4>
          {editing ? (
            <EditSegment segment={segment} onComplete={this.toggleEditing} />
          ) : (
            <DisplayData segment={segment} />
          )}
        </CardText>
      </Card>
    );
  }
}

const styles = {
  segmentDetailStyle: {
    margin: 5
  },
  segmentStatus: {
    width: "100%",
    display: "flex",
    alignItems: "center"
  },
  checkbox: {
    paddingLeft: 5,
    flex: "1 0 auto"
  },
  checkboxIconStyle: {
    paddingLeft: 3
  },
  detailColumns: {
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap"
  },
  detailColumn: {
    display: "flex",
    flexDirection: "column"
  }
};

export default SegmentDetails;



// WEBPACK FOOTER //
// /src/components/Trips/SegmentDetails.js